import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Card, CardContent } from '../../components/ui/card';
import { Atom, FlaskConical, Calculator, Target } from 'lucide-react';
import AllQs from '../../../public/Icons/AllSubjectsB&W.svg';
import rank1 from '../../../public/Icons/1strank.svg';
import rank2 from '../../../public/Icons/2ndrank.svg';
import rank3 from '../../../public/Icons/3rdrank.svg';

export function StudentCard({
  student,
  isSingleSubject,
  onCompare,
  allSubjects,
  topperStudent,
  isSingleSectionMultipleSubjects,
}) {
  const getRankDisplay = () => {
    if (student.rank === 1) return <img src={rank1} alt="1st Rank" />;
    if (student.rank === 2) return <img src={rank2} alt="2nd Rank" />;
    if (student.rank === 3) return <img src={rank3} alt="3rd Rank" />;
    return null;
  };

  const getCardStyle = () => {
    const baseStyle = {
      border: '1px solid var(--q3-stroke-normal)',
    };

    let finalStyle = {};
    const rankStyle = {};

    // Prioritize rank styling first (1st, 2nd, 3rd ranks get special styling)
    switch (student.rank) {
      case 1:
        finalStyle = {
          border: '1px solid transparent',
          background: `var(--rank1-bg) padding-box, var(--rank1-border) border-box`,
          boxShadow: 'none',
        };
        rankStyle.background = 'var(--rank1-rank-bg)';
        rankStyle.color = 'var(--rank1-rank-color)';
        break;
      case 2:
        finalStyle = {
          border: '1px solid transparent',
          background: `var(--rank2-bg) padding-box, var(--rank2-border) border-box`,
          boxShadow: 'none',
        };
        rankStyle.background = 'var(--rank2-rank-bg)';
        rankStyle.color = 'var(--rank2-rank-color)';
        break;
      case 3:
        finalStyle = {
          border: '1px solid transparent',
          background: `var(--rank3-bg) padding-box, var(--rank3-border) border-box`,
          boxShadow: 'none',
        };
        rankStyle.background = 'var(--rank3-rank-bg)';
        rankStyle.color = 'var(--rank3-rank-color)';
        break;
      default:
        // For ranks > 3, apply current user styling if applicable
        if (student.isCurrentUser) {
          finalStyle = {
            border: '2px solid transparent',
            background: `linear-gradient(var(--q3-surface-default), var(--q3-surface-default)) padding-box, linear-gradient(180deg, #EAF3FA 0%, var(--q3-surface-default) 100%) border-box`,
            boxShadow: 'none',
          };
          rankStyle.background = 'var(--q3-surface-dim)';
        }
        break;
    }

    return { cardStyle: { ...baseStyle, ...finalStyle }, rankStyle };
  };

  const { cardStyle, rankStyle } = getCardStyle();

  const studentName = student?.name;
  return (
    <Card
      onClick={() => !student.isCurrentUser && topperStudent.rank !== student.rank && onCompare(student)}
      style={cardStyle}
      className="rounded-3xl cursor-pointer hover:scale-[1.02] duration-300"
    >
      <CardContent className="px-4 py-2 flex flex-col justify-center items-center w-[264px] ">
        <div className="relative mb-4">
          <Avatar className="h-16 w-16 mx-auto mb-2 z-10">
            <AvatarImage src={student.avatar} alt={studentName} />
            <AvatarFallback style={{ backgroundColor: 'var(--q3-surface-dim)' }}>
              {studentName
                ?.split(' ')
                ?.map((n) => n[0])
                ?.join('')}
            </AvatarFallback>
          </Avatar>
          <div className="absolute z-8 bottom-[-14px] left-1/2 transform -translate-x-1/2 text-2xl">
            {getRankDisplay()}
          </div>
        </div>

        <h3 className="font-semibold text-base text-center mb-2 text-[var(--q3-neutral-default)] ">
          {studentName}
          {student.isCurrentUser && ' (You)'}
        </h3>

        <div
          className="mb-4 px-3 py-1 rounded-full inline-block"
          style={{
            ...rankStyle,
            backgroundColor:
              student.rank > 3 && !student.isCurrentUser ? 'var(--q3-surface-dim)' : rankStyle.background,
          }}
        >
          <span
            className="text-sm font-medium"
            style={{
              color: student.rank > 3 && !student.isCurrentUser ? 'var(--q3-surface-dim)' : rankStyle.color,
            }}
          >
            {student.rank}
            {student.rank === 1 ? 'st' : student.rank === 2 ? 'nd' : student.rank === 3 ? 'rd' : 'th'} Rank
          </span>
        </div>

        <div className="space-y-3 w-full text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={AllQs} alt="Score" className="h-4 w-4" />
              <span style={{ color: 'var(--q3-neutral-light)' }}>{isSingleSubject ? 'Score' : 'Overall Score'}</span>
            </div>
            <span>
              <span className="text-lg font-semibold text-[var(--q3-neutral-default)]">
                {Number.isInteger(student.overallScore) ? student.overallScore : student.overallScore?.toFixed(2)}
              </span>
              /<span className="text-[var(--q3-neutral-light)]">{student.maxScore}</span>
            </span>
          </div>
          {!isSingleSubject &&
            !isSingleSectionMultipleSubjects &&
            allSubjects &&
            allSubjects.map((subject) => {
              const subjectKey = subject.subjectId.shortName
                ? subject.subjectId.shortName.toLowerCase()
                : subject.subjectId.title.toLowerCase();
              const subjectScore = Number(student[subjectKey]);
              const subjectColor = subject.subjectId.color;
              return (
                subjectScore !== undefined && (
                  <div key={subject.subjectId._id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {subject.subjectId.icon && (
                        <img src={subject.subjectId.icon} alt={subject.subjectId.title} className="h-4 w-4" />
                      )}
                      <span style={{ color: 'var(--q3-neutral-light)' }}>{subject.subjectId.title} Score</span>
                    </div>
                    <span className="text-[var(--q3-neutral-light)]">
                      {Number.isInteger(subjectScore) ? subjectScore : subjectScore?.toFixed(2)}
                    </span>
                  </div>
                )
              );
            })}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4" style={{ color: 'var(--q3-purple-normal)' }} />
              <span style={{ color: 'var(--q3-neutral-light)' }}>Accuracy</span>
            </div>
            <span className="text-[var(--q3-neutral-light)]">
              {Number(student.accuracy)?.toFixed(2) || student.accuracy}%
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
